import { TrackerRect } from './barcode-tracker-selector';

/** Transform a rect from Quagga decoder space (the dimensions Quagga
 *  reports its boxes in — typically `Quagga.canvas.dom.overlay.width/height`,
 *  which under `locator.halfSample: true` is half the camera stream's
 *  intrinsic resolution) into the rendered DOM-pixel space of the
 *  `<video>` element.
 *
 *  The video element uses `object-fit: cover` (see
 *  client/src/pages/barcode-scanner-page/barcode-scanner-page.scss),
 *  meaning the source frame is uniformly scaled by the **maximum** of
 *  the two axis ratios (so it fully fills the box on both axes), and
 *  the excess is cropped equally off opposite edges. The offsets are
 *  therefore zero or negative — the source overflows the target.
 *  Do not flip the signs (a `contain`-fit transform would use `min`
 *  and produce positive offsets — that's the opposite shape).
 *
 *  Returns a zero rect on degenerate input (zero source or target
 *  dimensions) instead of crashing.
 */
export function transformDecoderRectToVideoSpace(
  rect: TrackerRect,
  sourceW: number,
  sourceH: number,
  targetW: number,
  targetH: number,
): TrackerRect {
  // require all four dims to be finite and positive — degenerate input
  // would otherwise produce NaN/Infinity in the cover-fit math below
  if (![sourceW, sourceH, targetW, targetH].every(n => Number.isFinite(n) && n > 0)) {
    return { x: 0, y: 0, w: 0, h: 0 };
  }
  // cover-fit: max ratio so the smaller-relative dim fills the target;
  // the larger-relative dim overflows and is cropped.
  const scale = Math.max(targetW / sourceW, targetH / sourceH);
  const offsetX = (targetW - sourceW * scale) / 2;
  const offsetY = (targetH - sourceH * scale) / 2;
  return {
    x: rect.x * scale + offsetX,
    y: rect.y * scale + offsetY,
    w: rect.w * scale,
    h: rect.h * scale,
  };
}
